/**
 * "Technical Performance" workspace tab strip.
 *
 * TWO MUTUALLY EXCLUSIVE SETS, not one strip with a divider. An organisation with
 * plant SCADA gets the plant screens; an organisation with market data only gets
 * the market screens. Both were shown side by side before, which put two
 * Performance tabs and two Availability tabs in the same strip — computed from
 * different sources, on different definitions, and never meant to reconcile.
 *
 * The market set is the "asset operating story" — outage history, forecast
 * accuracy, degradation. Peer-benchmark + benchmark-series moved to the unified
 * Benchmarks page (see Benchmarks.js) alongside the asset-benchmark summary that
 * used to live on Overview.
 *
 * The market routes still EXIST and still work; they are simply not offered when
 * plant data is the better answer. See MARKET_TO_SCADA for how a reader who
 * arrives on one is carried across.
 */

import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tabs, Tab, Box } from '@mui/material';

import useScadaCapability from './useScadaCapability';

const TABS = [
  { value: 'performance',       label: 'Performance',       path: 'performance' },
  // Curtailment directly after Performance — it's the deep-dive the
  // Performance page hands off to, and the centrepiece of the METIS story.
  // Outages third: the operational what-stopped-us read.
  { value: 'curtailment',       label: 'Curtailment',       path: 'curtailment' },
  { value: 'outages',           label: 'Outages',           path: 'outages' },
  { value: 'forecast-accuracy', label: 'Forecast accuracy', path: 'forecast-accuracy' },
  { value: 'availability',      label: 'Availability',      path: 'availability' },
  { value: 'degradation',       label: 'Degradation',       path: 'degradation' },
];

// Plant-internal SCADA. These REPLACE the market tabs above when the organisation
// has a client_* dataset granted: plant telemetry answers the same questions from
// the meter inwards, so showing both invites the reader to compare two numbers that
// are computed from different sources and were never meant to agree. For a
// market-only organisation the market tabs are all there is.
const SCADA_TABS = [
  { value: 'scada/performance',  label: 'Performance',       path: 'scada/performance' },
  { value: 'scada/production',   label: 'Production',        path: 'scada/production' },
  { value: 'scada/sensors',      label: 'Sensors',           path: 'scada/sensors' },
  { value: 'scada/availability', label: 'Availability',      path: 'scada/availability' },
  { value: 'scada/inverters',    label: 'Inverters',         path: 'scada/inverters' },
  { value: 'scada/strings',      label: 'Combiner boxes',    path: 'scada/strings' },
];

// Where a market tab's reader should land once the plant tabs have replaced it.
// Performance and Availability have direct SCADA counterparts; Curtailment,
// Outages, Forecast accuracy and Degradation have none, so they fall back to the
// SCADA Performance screen rather than to a blank strip.
const MARKET_TO_SCADA = {
  performance: 'scada/performance',
  availability: 'scada/availability',
};

function resolveActiveTab(pathname) {
  // SCADA paths are checked first: '/scada/performance' also contains
  // '/performance', which would otherwise match the market-data tab.
  const scada = SCADA_TABS.find((t) => pathname.includes(`/${t.path}`));
  if (scada) return scada.value;
  if (pathname.includes('/degradation'))         return 'degradation';
  if (pathname.includes('/forecast-accuracy'))   return 'forecast-accuracy';
  if (pathname.includes('/outages'))             return 'outages';
  if (pathname.includes('/curtailment'))         return 'curtailment';
  if (pathname.includes('/availability'))        return 'availability';
  if (pathname.includes('/performance'))         return 'performance';
  return 'performance';
}

export default function TechnicalWorkspaceTabs() {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Portfolio-scoped: the SCADA site's asset must be in THIS portfolio, not just
  // granted to the organisation.
  const { available: scadaAvailable, loading: capLoading } = useScadaCapability(portfolioId);

  const tabs = scadaAvailable ? SCADA_TABS : TABS;
  const active = resolveActiveTab(pathname);

  // The sidebar's "Technical Performance" entry points at /performance, the MARKET
  // page. Hiding that tab without redirecting would land every sidebar click — and
  // every existing bookmark — on a page whose tab is no longer in the strip, with no
  // way back but the URL bar. Waits for the capability answer, so a market-only org
  // is never bounced on the strength of a not-yet-loaded `false`.
  const stranded = scadaAvailable && TABS.some((t) => t.value === active);
  React.useEffect(() => {
    if (capLoading || !stranded) return;
    const target = MARKET_TO_SCADA[active] || 'scada/performance';
    navigate(`/portfolios/${portfolioId}/${target}`, { replace: true });
  }, [capLoading, stranded, active, navigate, portfolioId]);

  // Never hand MUI a value it has no Tab for — mid-load, or mid-redirect, the
  // pathname resolves to a tab that is not in this list, which logs an
  // out-of-range warning.
  const safeActive = tabs.some((t) => t.value === active) ? active : false;

  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mb: 2 }}>
      <Tabs
        value={safeActive}
        onChange={(_, v) => {
          const t = tabs.find((x) => x.value === v);
          if (t) navigate(`/portfolios/${portfolioId}/${t.path}`);
        }}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{ sx: { height: 2 } }}
        sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, py: 1 } }}
      >
        {/* No group divider any more: the two sets are mutually exclusive, so the
            strip only ever shows one of them and there is nothing to separate. */}
        {tabs.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>
    </Box>
  );
}
